import { getMissionbyIdApi, getProjectsBudgetTableApi } from '../../api/missionsApi'
import { getActionsBudgetTableApi, getProjetbyIdApi } from '../../api/projetApi'
import { getPlanbyIdApi } from '../../api/planApi'
import { useAsyncOptions } from '../../hooks/useAsyncOptions'
import { toNumber } from '../../utils/formatNumbers'
import { Mission } from '../../types/missionTypes'
import { Projet } from '../../types/projetTypes'
import { Plan } from '../../types/planTypes'

export type MissionBudgetInfo = {
  mission: Mission
  budget: number
  allocated: number
  remaining: number
  createdAt?: string | Date
}

export type ProjectBudgetInfo = {
  projet: Projet
  budget: number
  spent: number
  remaining: number
}

const sum = (rows?: { budget: number }[]) =>
  (rows ?? []).reduce((total, row) => total + (toNumber(row.budget) ?? 0), 0)

/** Mission budget, budget already allocated to its active projects, and creation date. */
export function useMissionBudget(missionId?: string, reloadKey = 0) {
  return useAsyncOptions<MissionBudgetInfo>(
    async () => {
      const [mission, table] = await Promise.all([
        getMissionbyIdApi(missionId as string),
        getProjectsBudgetTableApi(missionId as string).catch(() => ({ table: [] })),
      ])
      const budget = toNumber(mission.Budget) ?? 0
      const allocated = sum(table.table)
      return {
        mission,
        budget,
        allocated,
        remaining: budget - allocated,
        createdAt: mission.CreationDate ?? mission.Date,
      }
    },
    [missionId, reloadKey],
    Boolean(missionId)
  )
}

async function loadProjectBudget(projetId: string): Promise<ProjectBudgetInfo> {
  const [projet, table] = await Promise.all([
    getProjetbyIdApi(projetId),
    getActionsBudgetTableApi(projetId).catch(() => ({ table: [] })),
  ])
  const budget = toNumber(projet.Budget) ?? 0
  const spent = sum(table.table)
  return { projet, budget, spent, remaining: budget - spent }
}

function dayStamp(value: Date | string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/** True when a task cost or its dates fall outside the parent project. */
export function projectLimitViolation(
  info: ProjectBudgetInfo | null,
  input: { cost: number; start?: Date | string | null; end?: Date | string | null; countedCost?: number }
): 'cost' | 'dates' | null {
  if (!info) return null
  const ceiling = info.remaining + (input.countedCost ?? 0)
  if (Number(input.cost) > ceiling) return 'cost'
  const min = info.projet.DateDebut ? dayStamp(info.projet.DateDebut) : null
  const max = info.projet.DateFin ? dayStamp(info.projet.DateFin) : null
  const start = input.start ? dayStamp(input.start) : null
  const end = input.end ? dayStamp(input.end) : null
  if (min != null && start != null && start < min) return 'dates'
  if (max != null && end != null && end > max) return 'dates'
  if (min != null && end != null && end < min) return 'dates'
  if (max != null && start != null && start > max) return 'dates'
  return null
}

/** True when a deadline falls outside the treatment plan. */
export function deadlineOutsidePlan(
  deadline: Date | string | null | undefined,
  start?: Date | string | null,
  end?: Date | string | null
) {
  if (!deadline || !start || !end) return false
  const day = dayStamp(deadline)
  const min = dayStamp(start)
  const max = dayStamp(end)
  if (day == null || min == null || max == null) return false
  return day < min || day > max
}

/** Project budget, total cost of its active actions, and planning dates. */
export function useProjectBudget(projetId?: string, reloadKey = 0) {
  return useAsyncOptions<ProjectBudgetInfo>(
    () => loadProjectBudget(projetId as string),
    [projetId, reloadKey],
    Boolean(projetId)
  )
}

/** Budget info for several projects at once, keyed by project id. Failed lookups are skipped. */
export function useProjectBudgets(projetIds: string[]) {
  const key = Array.from(new Set(projetIds.filter(Boolean))).sort().join(',')
  return useAsyncOptions<Record<string, ProjectBudgetInfo>>(
    async () => {
      const ids = key.split(',')
      const results = await Promise.all(ids.map((id) => loadProjectBudget(id).catch(() => null)))
      return ids.reduce<Record<string, ProjectBudgetInfo>>((acc, id, i) => {
        const info = results[i]
        if (info) acc[id] = info
        return acc
      }, {})
    },
    [key],
    Boolean(key)
  )
}

export function usePlanInfo(planId?: string) {
  return useAsyncOptions<Plan>(() => getPlanbyIdApi(planId as string), [planId], Boolean(planId))
}

/** "YYYY-MM-DD" in local time, for <input type="date">. */
export function toInputDate(value?: string | Date | null): string {
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function fromInputDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d, 12)
}
